/**
 * TodoCreateHost — the engine-backed full create modal, mountable anywhere
 * (the to-dos upgrade, 2026-10-04).
 *
 * Used by the to-dos page ("+ Full to-do" and an action to-do typed into the
 * quick-add line) and by the top bar's "+ Add a to-do", which carries the page
 * the person is on in as `prefill` — client, record, contact and team (Michael:
 * "depending on where this todo is coming from, it'll auto populate the
 * information"). The form is the canonical `TodoCreateContainer`; this file is
 * only the write: one POST through `createInputFromDraft`, the call binding for
 * a booking to-do, then `onCreated`.
 *
 * The button is the design's "+ Create to-do": the new to-do lands in the list
 * with its verb on the row, rather than chaining straight into a composer.
 *
 * The to-do sweep (2026-10-05; Michael: "ensure that the modal is shared across
 * all surfaces that can generate a todo — some creation points create different
 * types of todos or open the wrong modal"). The L10's "+ Add to-do" and a
 * rock's "Add a to-do" opened a second, older dialog with its own list of
 * types (Phone Call, Milestone). They mount THIS now:
 *
 *   - `fixedRecord` — the record the to-do is being made on, shown as its
 *     provenance and not editable (an L10 meeting, a rock, a milestone).
 *   - `write` — a surface whose people may not hold the to-dos permission
 *     saves through its own door (the L10's meeting-scoped create, gated on
 *     meeting participation). It is handed the SAME body `POST /api/todos`
 *     would get, so no surface builds a to-do by hand again. Such a door takes
 *     no client, record or call binding, so the form does not offer them.
 *
 * Any new surface that lets a person write a to-do mounts this file. A second
 * form is the defect `todoCreateDoors.test.ts` exists to stop.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TodoCreateContainer, { type TodoCreateDraft, type TodoCreatePrefill } from './TodoCreateContainer';
import type { TodoSourceRecord } from './TodoSourceRow';
import { createInputFromDraft } from './todoCreateInput';
import { todoDueLabel } from './todoDue';
import { todosApi, type CreateTodoInput, type TodoRow } from '../../services/todosApi';
import { engineTodosDataSource } from '../engine/dataSources/engineTodosDataSource';
import type { AssigneeOption } from '../shared/InlineEdit/types';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

export type { TodoCreatePrefill } from './TodoCreateContainer';

export default function TodoCreateHost({ open, prefill, onClose, onCreated, announce = false, fixedRecord = null, write }: {
  open: boolean;
  prefill: TodoCreatePrefill | null;
  onClose: () => void;
  /** The new row — the to-dos page lands it in the list. */
  onCreated?: (row: TodoRow) => void;
  /** Off the to-dos page: confirm with a toast that links to the new to-do. */
  announce?: boolean;
  /** The record this to-do is being made ON — provenance in the SOURCE row, not editable. */
  fixedRecord?: TodoSourceRecord | null;
  /** Save through the surface's own door instead of `POST /api/todos` (see the header). */
  write?: (input: CreateTodoInput) => Promise<TodoRow>;
}): React.ReactElement {
  const auth = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [assignees, setAssignees] = useState<AssigneeOption[]>([]);

  useEffect(() => {
    if (!open || assignees.length) return undefined;
    let alive = true;
    engineTodosDataSource.getAssignees().then((opts) => { if (alive) setAssignees(opts); }).catch(() => { /* picker shows Unassigned + me */ });
    return () => { alive = false; };
  }, [open, assignees.length]);

  const handleCreate = useCallback(async (draft: TodoCreateDraft) => {
    const input = createInputFromDraft(draft, { category: 'internal' });
    const binds = draft.task_type === 'schedule_call' && (draft.event_template_id != null || draft.event_type_id != null);
    // A record's own door that binds the call in the same step (the ticket's does)
    // reads these from the body; a door that ignores them leaves the to-do bindable later.
    const row = write
      ? await write(binds ? { ...input, event_template_id: draft.event_template_id ?? null, event_type_id: draft.event_type_id ?? null } as CreateTodoInput : input)
      : (await todosApi.create(input)).data;
    if (!write && binds) {
      // Fail-soft: a to-do that lands without its call binding is bindable later.
      try { await todosApi.setCallType(row.id, draft.event_template_id, draft.event_type_id); } catch { /* bindable later */ }
    }
    onCreated?.(row);
    onClose();
    if (announce) {
      const who = draft.assignee_id != null && draft.assignee_id !== auth.user?.id
        ? assignees.find((a) => a.id === draft.assignee_id)?.name?.split(/\s+/)[0]
        : null;
      const when = draft.due_date ? todoDueLabel(draft.due_date, draft.due_suggested) : 'needs a date';
      toast.showToast(`${who ? `Added for ${who}` : 'Added to your to-dos'} · ${when}`, 'success', {
        action: { label: 'Open', onClick: () => navigate(`/todos?todo=${row.id}`) },
      });
    }
  }, [write, onCreated, onClose, announce, auth.user?.id, assignees, toast, navigate]);

  return (
    <TodoCreateContainer
      open={open}
      onClose={onClose}
      onCreate={handleCreate}
      assigneeOptions={assignees}
      currentUserId={auth.user?.id ?? null}
      // A surface's own door takes no call binding and no client / record link.
      // An EOS to-do (an L10's, a rock's) belongs to that record alone: the form
      // shows it and does not offer to re-point it at a project.
      supportsCallTypes={!write}
      supportsProjectLink={!write && fixedRecord?.kind !== 'EOS'}
      fixedRecord={fixedRecord}
      prefill={prefill}
      defaultList="standalone"
      ariaLabel="New to-do"
    />
  );
}
